"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import Image from "next/image";
import Link from "next/link";
import { FiStar, FiArrowRight, FiX } from "react-icons/fi";
import { TEAMS } from "@/components/team_selector/teams";
import { trackReaderEvent } from "@/lib/reader-analytics";
import { getFavoriteTeam, getServerFavoriteTeam, setFavoriteTeam, subscribeFavoriteTeam } from "@/lib/favorite-team";
import styles from "./favorite_team.module.css";

/** 球団登録または選択付きショートカットを返す。端末設定は共通購読し、書き込みはユーザー操作時のみ行う。 */
export default function FavoriteTeam({ teamSlug, compact = false }: { teamSlug?: string; compact?: boolean }) {
	const favorite = useSyncExternalStore(subscribeFavoriteTeam, getFavoriteTeam, getServerFavoriteTeam);
	const [message, setMessage] = useState("");
	const [failed, setFailed] = useState(false);
	const [open, setOpen] = useState(false);
	const details = useRef<HTMLDetailsElement>(null);
	const selected = TEAMS.find(team => team.key === favorite);

	/** 開いている選択欄を閉じ、必要な場合だけ操作元へフォーカスを戻す。 */
	function closePicker(focus = false) {
		if (!details.current) return;
		details.current.open = false;
		setOpen(false);
		if (focus) details.current.querySelector("summary")?.focus();
	}

	useEffect(() => {
		if (!open) return;
		/** 選択欄の外を押したときは、移動先の操作を妨げず閉じる。 */
		function onPointer(event: PointerEvent) {
			if (event.target instanceof Node && !details.current?.contains(event.target)) closePicker();
		}
		/** Escapeで選択を変更せず閉じ、操作元へ戻す。 */
		function onKey(event: KeyboardEvent) {
			if (event.key === "Escape") closePicker(true);
		}
		document.addEventListener("pointerdown", onPointer);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("pointerdown", onPointer);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	/** 球団を保存・解除する。失敗時は選択欄を残す。成功時は購読者を更新し、操作を計測する。 */
	function save(slug: string) {
		if (!setFavoriteTeam(slug)) {
			setFailed(true);
			setMessage("保存できませんでした。ブラウザーの保存設定をご確認ください。");
			return;
		}
		trackReaderEvent("favorite_team_change", { team: slug || favorite, action: slug ? "save" : "remove" });
		setFailed(false);
		setMessage(slug ? "マイ球団を変更しました" : "マイ球団を解除しました");
		closePicker(true);
	}

	if (teamSlug) return (
		<div className={styles.favoriteAction}>
			<button type="button" aria-pressed={favorite === teamSlug} onClick={() => save(favorite === teamSlug ? "" : teamSlug)} className={styles.favoriteButton}>
				<FiStar aria-hidden="true" fill={favorite === teamSlug ? "currentColor" : "none"} />
				{favorite === teamSlug ? "マイ球団に登録済み" : "マイ球団に登録"}
			</button>
			<span className={failed ? styles.message : "sr-only"} role="status">{message}</span>
		</div>
	);

	return (
		<div className={compact ? styles.compact : styles.shortcut}>
			<div className={styles.shortcutRow}>
				{selected && (
					<Link href={`/team/${selected.key}#latest`} className={styles.favoriteShortcut} aria-label={`マイ球団：${selected.shortName}の記事`}>
						<Image src={selected.icon} width={24} height={24} alt="" unoptimized />
						{compact && <span className={styles.label}>マイ球団</span>}
						<span>{selected.shortName}</span>
						<FiArrowRight aria-hidden="true" className={styles.arrow} />
					</Link>
				)}
				<details ref={details} className={styles.details} onToggle={event => setOpen(event.currentTarget.open)}>
					<summary aria-label={selected ? "マイ球団を変更" : "マイ球団を選択"} className={styles.change}>
						{!selected && <FiStar aria-hidden="true" />}
						{selected ? "変更" : "マイ球団を選択"}
					</summary>
					<div className={styles.picker}>
						<div className={styles.pickerHeading}>
							<strong>マイ球団</strong>
							<button type="button" onClick={() => closePicker(true)} aria-label="球団選択を閉じる"><FiX aria-hidden="true" /></button>
						</div>
						<div className={styles.teamOptions} role="group" aria-label="マイ球団の選択">
							{TEAMS.map(team => (
								<button key={team.key} type="button" aria-pressed={favorite === team.key} onClick={() => save(team.key)}>
									<Image src={team.icon} width={28} height={28} alt="" unoptimized />
									<span>{team.shortName}</span>
								</button>
							))}
						</div>
						{selected && <button type="button" onClick={() => save("")} className={styles.remove}>登録を解除</button>}
					</div>
				</details>
			</div>
			<span className={failed ? styles.message : "sr-only"} role="status">{message}</span>
		</div>
	);
}
